'use client';
import { useImageFallback } from '@susu/custom-made/hooks/useImageFallback';
import { FALLBACK_IMAGE_CLASSES, FALLBACK_IMAGE_CONTAINER_CLASSES } from '@susu/custom-made/utils/fallbackImage';
import type { Fabric, MonogramColor, Option, OptionValue } from '@susu/custom-made/utils/interfaces';
import { CARD_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import { memo, useRef } from 'react';

export enum CardSize {
  Medium = 'medium',
  Small = 'small',
}

export enum CardOrientation {
  Default = 'default',
  Vertical = 'vertical',
}

export enum CardInfoIconType {
  Light = 'light',
  Dark = 'dark',
}

export type typeCard = Fabric | Option | OptionValue | MonogramColor;

const GRID_LG = {
  container: 'lg:flex lg:flex-col lg:items-stretch lg:m-0 lg:p-0 lg:w-full lg:text-left lg:border-0',
  imageBox: 'lg:w-full lg:p-0 lg:border-0 lg:rounded-md lg:aspect-[169.33/138.9]',
  ring: "lg:after:content-[''] lg:after:absolute lg:after:rounded-lg lg:after:pointer-events-none",
  ringSelected: 'lg:after:-inset-3 lg:after:border lg:after:border-gray-600',
  ringWarning: 'lg:after:-inset-5 lg:after:border-2 lg:after:border-red-600',
  containerFocus: 'lg:focus-visible:shadow-none',
  imageFocus: 'lg:group-focus-visible:shadow-[0px_0px_0px_4px_var(--color-primary-opacity-30)]',
  image: 'lg:absolute lg:inset-0 lg:w-full lg:h-full',
  details: 'lg:pt-10 lg:px-0 lg:pb-0 lg:text-left lg:visible',
  text: 'lg:text-sm lg:leading-sm lg:font-medium lg:overflow-visible lg:whitespace-normal',
  subText: 'lg:text-sm lg:leading-sm lg:font-light lg:pt-2',
} as const;

/**
 * `content-visibility: auto` lets the browser skip layout and paint for the cards scrolled
 * out of view. The search list stacks up to ~400 of these, and re-filtering unmounts and
 * remounts the ones that dropped out, so paying full layout for every result is what made
 * typing feel laggy on mobile (CFS-16698). `contain-intrinsic-size` supplies the placeholder
 * height used while a card is skipped — the `auto` keyword makes the browser remember each
 * card's real height once rendered, so the scrollbar settles instead of drifting. The
 * mobile/tablet figures track the image box (88px, 116px at md) plus the 3px padding.
 * Scoped below `lg`, where this variant becomes a multi-column grid item with a different
 * height and the list is short enough not to need it.
 */
const CARD_CONTAINER_VERTICAL_MOBILE =
  'group text-center cursor-pointer relative flex items-center w-auto rounded-[9px] m-3 focus:outline-none focus-visible:shadow-[0px_0px_0px_3px_var(--color-primary-opacity-30)] [content-visibility:auto] [contain-intrinsic-size:auto_94px] md:[contain-intrinsic-size:auto_122px] lg:[content-visibility:visible]';
const IMAGE_CONTAINER_VERTICAL_MOBILE = 'relative p-0 rounded-[7px] w-[88px] md:w-[116px]';
const CARD_DETAILS_VERTICAL_MOBILE = 'block text-left py-0 px-16';
const CARD_TEXT_VERTICAL_MOBILE = 'overflow-auto whitespace-normal text-[13px] font-medium';
const CARD_SUBTEXT_VERTICAL_MOBILE = 'block text-xs font-light';

/**
 * CFS-16840: option and fabric names arrive already-translated from the CDP service and their
 * casing varies per locale (Dutch returns "knoop" lowercase while its siblings are capitalized).
 * Force the first letter uppercase in CSS so every card title reads consistently in any locale,
 * without rewriting the string — `data-test-section` attributes and Segment/Avo tracking read the
 * same `name` values. Deliberately `first-letter:uppercase` and not `capitalize`: the latter
 * uppercases every word, turning "knoop met gaatjes" into "Knoop Met Gaatjes".
 */
const CARD_TEXT_CAPITALIZE = 'first-letter:uppercase';

/**
 * `isPresentationOnly`, `isGrid` and `isVertical` never combine in more than these five ways, so
 * every style getter below switches on this one derived variant instead of each repeating the
 * same `isPresentationOnly → isGrid ? isVertical : … → isVertical → default` cascade.
 */
type CardVariant = 'presentation' | 'gridVertical' | 'gridDefault' | 'vertical' | 'default';

const getCardVariant = (isPresentationOnly: boolean, isGrid: boolean, isVertical: boolean): CardVariant => {
  if (isPresentationOnly) return 'presentation';
  if (isGrid) return isVertical ? 'gridVertical' : 'gridDefault';
  return isVertical ? 'vertical' : 'default';
};

const getCardContainerClasses = (isSmall: boolean, isSelected: boolean, isWarning: boolean, variant: CardVariant) => {
  switch (variant) {
    case 'presentation':
      return 'group text-center cursor-pointer relative flex items-center w-auto rounded-[9px] m-0 py-6 px-0 md:py-8 focus:outline-none';
    case 'gridVertical':
      return cn(CARD_CONTAINER_VERTICAL_MOBILE, GRID_LG.container, GRID_LG.containerFocus, 'lg:rounded-none', {
        'py-2 px-2 border border-gray-600': isSelected,
        'py-3 px-3': !isSelected,
      });
    case 'gridDefault':
      return cn(
        'group text-center cursor-pointer relative py-3 px-2 md:py-4 md:px-4 focus:outline-none',
        GRID_LG.container,
        isSmall ? 'w-[84px] md:w-[118px]' : 'w-[100px] md:w-[134px]',
      );
    case 'vertical':
      return cn(
        'group text-center cursor-pointer relative flex items-center w-auto rounded-[9px] m-3 lg:m-6 2xl:m-8 focus:outline-none',
        'focus-visible:shadow-[0px_0px_0px_3px_var(--color-primary-opacity-30)] lg:focus-visible:shadow-[0px_0px_0px_4px_var(--color-primary-opacity-30)]',
        {
          'py-2 px-2 lg:py-3 lg:px-3 border border-gray-600 lg:border-2': isSelected,
          'py-3 px-3': !isSelected,
          'lg:py-2 lg:px-2 lg:border-2 lg:border-red-600': !isSelected && isWarning,
          'lg:py-5 lg:px-5': !isSelected && !isWarning,
        },
      );
    default:
      return cn(
        'group text-center cursor-pointer relative py-3 px-2 md:py-4 md:px-4 focus:outline-none',
        'lg:flex lg:items-center lg:m-6 lg:w-auto lg:rounded-[9px] 2xl:m-8',
        'lg:focus-visible:shadow-[0px_0px_0px_4px_var(--color-primary-opacity-30)]',
        isSmall ? 'w-[84px] md:w-[118px] lg:w-auto' : 'w-[100px] md:w-[134px] lg:w-auto',
        {
          'lg:py-5 lg:px-5': !isSelected && !isWarning,
          'lg:py-2 lg:px-2 lg:border-2 lg:border-red-600': isWarning && !isSelected,
          'lg:py-3 lg:px-3 lg:border-2 lg:border-gray-600': isSelected,
        },
      );
  }
};

/** Shared by both grid branches of `getImageContainerClasses` below. */
const getGridRingClasses = (isSelected: boolean, isWarning: boolean) =>
  cn((isSelected || isWarning) && GRID_LG.ring, {
    [GRID_LG.ringSelected]: isSelected,
    [GRID_LG.ringWarning]: isWarning && !isSelected,
  });

const getImageContainerClasses = (
  isSmall: boolean,
  isSelected: boolean,
  isWarning: boolean,
  variant: CardVariant,
  isFallbackImage?: boolean,
) => {
  const fallbackBgClasses = isFallbackImage && FALLBACK_IMAGE_CONTAINER_CLASSES;

  switch (variant) {
    case 'presentation':
      return cn(
        'relative p-0 rounded-[7px] w-[64px] md:w-[100px] lg:rounded-[9px] lg:w-[80px] lg:h-[80px] 2xl:w-[100px] 2xl:h-[100px]',
        fallbackBgClasses,
      );
    case 'gridVertical':
      return cn(
        IMAGE_CONTAINER_VERTICAL_MOBILE,
        GRID_LG.imageBox,
        GRID_LG.imageFocus,
        getGridRingClasses(isSelected, isWarning),
        fallbackBgClasses,
      );
    case 'gridDefault':
      return cn(
        'relative rounded-[7px] md:rounded-[9px]',
        isSmall ? 'w-[78px] md:w-[110px]' : 'w-[94px] md:w-[126px]',
        'group-focus-visible:shadow-[0px_0px_0px_3px_var(--color-primary-opacity-30)] md:group-focus-visible:shadow-[0px_0px_0px_4px_var(--color-primary-opacity-30)]',
        GRID_LG.imageBox,
        {
          'p-3 md:p-5': !isSelected && !isWarning,
          'p-2 border-2 border-red-600': isWarning && !isSelected,
          'p-2 md:p-3 border border-gray-600 md:border-2': isSelected,
        },
        getGridRingClasses(isSelected, isWarning),
        fallbackBgClasses,
      );
    case 'vertical':
      return cn(
        'relative p-0 rounded-[7px] w-[88px] md:w-[116px] lg:rounded-[9px] lg:w-[116px] lg:h-[116px] 2xl:w-[140px] 2xl:h-[140px]',
        fallbackBgClasses,
      );
    default:
      return cn(
        'relative rounded-[7px] md:rounded-[9px] lg:p-0',
        isSmall
          ? 'w-[78px] md:w-[110px] lg:w-[116px] lg:h-[116px] 2xl:w-[140px] 2xl:h-[140px]'
          : 'w-[94px] md:w-[126px] lg:w-[116px] lg:h-[116px] 2xl:w-[140px] 2xl:h-[140px]',
        'group-focus-visible:shadow-[0px_0px_0px_3px_var(--color-primary-opacity-30)] md:group-focus-visible:shadow-[0px_0px_0px_4px_var(--color-primary-opacity-30)] lg:group-focus-visible:shadow-none',
        {
          'p-3 md:p-5': !isSelected && !isWarning,
          'p-2 border-2 border-red-600 lg:border-0': isWarning && !isSelected,
          'p-2 md:p-3 border border-gray-600 md:border-2 lg:border-0': isSelected,
        },
        fallbackBgClasses,
      );
  }
};

const getImageCountClasses = (count: number, isSmall: boolean) => {
  switch (count) {
    case 4:
      return cn(
        'flex flex-wrap [&>img]:rounded-none [&>img:nth-child(1)]:rounded-tl-md [&>img:nth-child(2)]:rounded-tr-md [&>img:nth-child(3)]:rounded-bl-md [&>img:nth-child(4)]:rounded-br-md',
        isSmall
          ? '[&>img]:w-[36px] [&>img]:h-[36px] md:[&>img]:w-[50px] md:[&>img]:h-[50px] lg:[&>img]:w-[58px] lg:[&>img]:h-[58px] 2xl:[&>img]:w-[70px] 2xl:[&>img]:h-[70px]'
          : '[&>img]:w-[44px] [&>img]:h-[44px] md:[&>img]:w-[58px] md:[&>img]:h-[58px] 2xl:[&>img]:w-[70px] 2xl:[&>img]:h-[70px]',
      );
    case 3:
      return 'flex flex-wrap [&>img]:rounded-none [&>img:nth-child(1)]:rounded-t-md [&>img:nth-child(2)]:rounded-bl-md [&>img:nth-child(3)]:rounded-br-md [&>img]:w-[44px] [&>img]:h-[44px] md:[&>img]:w-[58px] md:[&>img]:h-[58px] 2xl:[&>img]:w-[70px] 2xl:[&>img]:h-[70px] [&>img:nth-child(1)]:w-[88px] md:[&>img:nth-child(1)]:w-[116px] 2xl:[&>img:nth-child(1)]:w-[140px]';
    case 2:
      return 'flex flex-wrap [&>img]:rounded-none [&>img:nth-child(1)]:rounded-t-md [&>img:nth-child(2)]:rounded-b-md [&>img]:w-[88px] [&>img]:h-[44px] md:[&>img]:w-[116px] md:[&>img]:h-[58px] 2xl:[&>img]:w-[140px] 2xl:[&>img]:h-[70px]';
    default:
      return '';
  }
};

const getCardImageObjectFitClasses = (isFallbackImage?: boolean) =>
  isFallbackImage ? FALLBACK_IMAGE_CLASSES : 'object-cover';

const getCardImageClasses = (isSmall: boolean, variant: CardVariant, isFallbackImage?: boolean) => {
  const objectFitClasses = getCardImageObjectFitClasses(isFallbackImage);

  switch (variant) {
    case 'presentation':
      return cn(
        'block rounded-md w-[64px] h-[64px] md:w-[100px] md:h-[100px] lg:w-[80px] lg:h-[80px] 2xl:w-[100px] 2xl:h-[100px]',
        objectFitClasses,
      );
    case 'gridVertical':
      return cn('block rounded-md w-[88px] h-[88px] md:w-[116px] md:h-[116px]', objectFitClasses, GRID_LG.image);
    case 'gridDefault':
      return cn(
        'block rounded-md',
        objectFitClasses,
        isSmall ? 'w-72 h-72 md:w-[100px] md:h-[100px]' : 'w-[88px] h-[88px] md:w-[116px] md:h-[116px]',
        GRID_LG.image,
      );
    case 'vertical':
      return cn(
        'block rounded-md w-[88px] h-[88px] md:w-[116px] md:h-[116px] lg:w-[116px] lg:h-[116px] 2xl:w-[140px] 2xl:h-[140px]',
        objectFitClasses,
      );
    default:
      return cn(
        'block rounded-md',
        objectFitClasses,
        isSmall
          ? 'w-72 h-72 md:w-[100px] md:h-[100px] lg:w-[116px] lg:h-[116px] 2xl:w-[140px] 2xl:h-[140px]'
          : 'w-[88px] h-[88px] md:w-[116px] md:h-[116px] 2xl:w-[140px] 2xl:h-[140px]',
      );
  }
};

const getPriceContainerClasses = () =>
  cn(
    'absolute top-12 left-[9px] flex items-center justify-center bg-white py-2 px-6 rounded-[80px] text-xs font-normal leading-[12px] z-[1] md:top-16 md:left-[13px] md:px-[7px] md:text-xs md:leading-xs lg:top-6 lg:left-6',
  );

const getBadgeClasses = (isSelected: boolean, variant: CardVariant) =>
  cn(
    'absolute flex h-20 items-center rounded-full bg-surface px-6 text-xs leading-xs font-normal text-text whitespace-nowrap pointer-events-none shadow-[0px_4px_10px_rgba(0,0,0,0.12)] lg:top-8 lg:left-8',
    variant === 'gridDefault'
      ? {
          'top-6 left-6 md:top-[9px] md:left-[9px]': isSelected,
          'top-[7px] left-[7px] md:top-[11px] md:left-[11px]': !isSelected,
        }
      : 'top-4 left-4 md:top-6 md:left-6',
  );

const getInfoSpaceClasses = (isSelected: boolean) =>
  cn(
    'lg:group-hover:absolute lg:group-hover:bottom-6 lg:group-hover:right-6 lg:group-hover:w-24 lg:group-hover:h-24 lg:group-hover:cursor-pointer',
    {
      'absolute bottom-0 right-0 w-44 h-44 cursor-pointer md:bottom-[11px] md:right-[11px] md:w-20 md:h-20 lg:bottom-6 lg:right-6 lg:w-24 lg:h-24':
        isSelected,
    },
  );

const getInfoIconClasses = (isSelected: boolean, isDark: boolean) =>
  cn(
    'absolute bottom-[7px] right-[7px] w-20 h-20 rounded-full justify-center items-center text-base text-gray-800 border border-transparent shadow-[0px_3px_4px_0px_var(--color-primary-opacity-30)] [&>i]:font-bold md:bottom-0 md:right-0 lg:bottom-2 lg:right-2 lg:w-20 lg:h-20 lg:text-base lg:group-hover:flex',
    "after:content-[''] after:absolute after:-top-2 after:-bottom-2 after:-left-2 after:-right-2 after:rounded-full after:z-[-1]",
    { hidden: !isSelected, flex: isSelected },
    isDark
      ? 'bg-[rgba(121,121,121,0.3)] backdrop-blur-[8px] [&>i]:text-white after:bg-[linear-gradient(180deg,rgba(121,121,121,0.1)_0%,rgba(121,121,121,0.3)_100%)]'
      : 'bg-white [&>i]:text-gray-800 after:bg-[linear-gradient(180deg,rgba(121,121,121,0.2)_0%,rgba(121,121,121,0)_100%)]',
  );

const getCardDetailsClasses = (hideMobileInfoText: boolean, variant: CardVariant) => {
  switch (variant) {
    case 'presentation':
      return 'block text-left py-0 px-12 md:px-20 lg:px-20 2xl:px-24';
    case 'gridVertical':
      return cn(CARD_DETAILS_VERTICAL_MOBILE, GRID_LG.details);
    case 'gridDefault':
      return cn('block pt-8 text-center', GRID_LG.details, { 'invisible pb-16': hideMobileInfoText });
    case 'vertical':
      return 'block text-left py-0 px-16 lg:pt-20 lg:px-20 lg:pb-20 2xl:pt-24 2xl:px-24 2xl:pb-24';
    default:
      return cn('block pt-8 text-center lg:pt-20 lg:px-20 lg:pb-20 lg:text-left lg:visible 2xl:pt-24 2xl:px-24 2xl:pb-24', {
        'invisible pb-16': hideMobileInfoText,
      });
  }
};

const getCardTextClasses = (isSelected: boolean, isWarning: boolean, variant: CardVariant) => {
  switch (variant) {
    case 'presentation':
      return cn(
        'overflow-auto whitespace-normal font-medium text-sm leading-sm md:text-base md:leading-base lg:text-sm lg:leading-sm 2xl:text-base 2xl:leading-base',
        isWarning ? 'text-red-600' : 'text-gray-800',
      );
    case 'gridVertical':
      return cn(
        CARD_TEXT_VERTICAL_MOBILE,
        GRID_LG.text,
        isWarning ? 'text-red-600' : 'text-gray-800 lg:group-hover:text-gray-600',
      );
    case 'gridDefault':
      return cn(
        'overflow-hidden text-ellipsis whitespace-nowrap text-[11px] md:text-sm',
        { 'font-medium': isSelected, 'font-light': !isSelected },
        GRID_LG.text,
        isWarning ? 'text-red-600' : 'text-gray-800 lg:group-hover:text-gray-600',
      );
    case 'vertical':
      return cn(
        'overflow-auto whitespace-normal text-[13px] font-medium lg:text-sm 2xl:text-base',
        isWarning ? 'text-red-600' : 'text-gray-800 lg:group-hover:text-gray-600',
      );
    default:
      return cn(
        'overflow-hidden text-ellipsis whitespace-nowrap text-[11px] md:text-sm lg:text-sm lg:font-medium lg:overflow-auto lg:whitespace-normal 2xl:text-base',
        { 'font-medium': isSelected, 'font-light': !isSelected },
        isWarning ? 'text-red-600' : 'text-gray-800 lg:group-hover:text-gray-600',
      );
  }
};

const getCardSubTextClasses = (isWarning: boolean, variant: CardVariant) => {
  switch (variant) {
    case 'presentation':
      return cn(
        'block text-sm font-light leading-sm lg:text-xs lg:pt-2 2xl:text-sm',
        isWarning ? 'text-red-600' : 'text-gray-800',
      );
    case 'gridVertical':
      return cn(
        CARD_SUBTEXT_VERTICAL_MOBILE,
        GRID_LG.subText,
        isWarning ? 'text-red-600' : 'text-gray-800 lg:group-hover:text-gray-600',
      );
    case 'gridDefault':
      return cn('hidden lg:block', GRID_LG.subText, isWarning ? 'text-red-600' : 'text-gray-800 lg:group-hover:text-gray-600');
    case 'vertical':
      return cn(
        'block text-xs font-light lg:pt-2 2xl:text-sm',
        isWarning ? 'text-red-600' : 'text-gray-800 lg:group-hover:text-gray-600',
      );
    default:
      return cn(
        'hidden lg:block lg:text-xs lg:font-light lg:pt-2 2xl:text-sm',
        isWarning ? 'text-red-600' : 'text-gray-800 lg:group-hover:text-gray-600',
      );
  }
};

const getArrowIconClasses = (variant: CardVariant) => {
  switch (variant) {
    case 'presentation':
      return 'hidden';
    case 'gridVertical':
      return 'block absolute top-[calc(50%-10px)] right-20 w-20 h-20 lg:hidden';
    case 'gridDefault':
      return 'hidden';
    case 'vertical':
      return 'block absolute top-[calc(50%-10px)] right-20 w-20 h-20 lg:text-gray-800';
    default:
      return 'hidden lg:block lg:absolute lg:top-[calc(50%-10px)] lg:right-20 lg:w-20 lg:h-20 lg:text-gray-800';
  }
};

interface CardProps {
  card: typeCard;
  cardImages: string[];
  cardText: string;
  tabIndex?: number;
  cardSubText?: string;
  cardSubAdditionalText?: string;
  cardSize?: CardSize;
  cardOrientation?: CardOrientation;
  isParent?: boolean;
  isSelected?: boolean;
  hideMobileInfoText?: boolean;
  showInfoIcon?: boolean;
  showTooltip?: boolean;
  cardInfoIconType?: CardInfoIconType;
  cardPrice?: string;
  badgeText?: string;
  isPresentationOnly?: boolean;
  showWarning?: boolean;
  cardFallbackImageUrl?: string;
  isGrid?: boolean;
  cardClick: (card: typeCard) => void;
  infoIconClick?: (card: typeCard) => void;
}

export const Card = memo(
  ({
    card,
    cardImages,
    cardText,
    tabIndex,
    cardSubText,
    cardSubAdditionalText,
    cardSize,
    cardOrientation,
    isParent,
    isSelected,
    hideMobileInfoText,
    showInfoIcon,
    showTooltip,
    cardInfoIconType,
    cardPrice,
    badgeText,
    isPresentationOnly,
    showWarning,
    cardFallbackImageUrl,
    isGrid = false,
    cardClick,
    infoIconClick,
  }: CardProps) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const { genericFallbackImages, handleImageError, handleImageLoad } = useImageFallback();
    const hasFailedImage = Object.keys(genericFallbackImages).length > 0;

    const isSmall = cardSize === CardSize.Small;
    const isVertical = cardOrientation === CardOrientation.Vertical;
    const isDark = cardInfoIconType === CardInfoIconType.Dark;
    const selected = !!isSelected;
    const presentation = !!isPresentationOnly;
    const warning = !!showWarning;
    const variant = getCardVariant(presentation, isGrid, isVertical);

    const infoClicked = (e: React.BaseSyntheticEvent) => {
      if (showTooltip) {
        //Show tooltip
        const elementRect = e.target.getBoundingClientRect();

        cardRef.current?.dispatchEvent(
          new CustomEvent('tooltipOpened', {
            detail: {
              tooltipId: 'optionPanel',
              title: cardText,
              description: cardSubText,
              topPosition: elementRect.top,
              leftPosition: elementRect.left,
              width: elementRect?.width,
            },
            bubbles: true,
          }),
        );
      } else if (infoIconClick) {
        infoIconClick(card);
      }
    };

    return (
      <div
        className={getCardContainerClasses(isSmall, selected, warning, variant)}
        ref={cardRef}
        data-testid={card.id}
        onClick={() => cardClick(card)}
        onKeyDown={e => {
          onEnter(e, () => {
            cardClick(card);
          });
        }}
        tabIndex={tabIndex}
      >
        {cardPrice && (
          <div className={getPriceContainerClasses()} data-testid={CARD_TEST_IDS.PRICE}>
            {cardPrice}
          </div>
        )}
        <div
          className={cn(
            getImageContainerClasses(isSmall, selected, warning, variant, hasFailedImage),
            getImageCountClasses(cardImages.length, isSmall),
          )}
        >
          {cardImages.map((imagePath: string) => (
            <img
              key={imagePath}
              className={getCardImageClasses(isSmall, variant, !!genericFallbackImages[imagePath])}
              loading="lazy"
              src={imagePath}
              alt={imagePath}
              onError={e => handleImageError(e, imagePath, cardFallbackImageUrl)}
              onLoad={e => handleImageLoad(e, imagePath)}
            />
          ))}
          {badgeText && (
            <span className={getBadgeClasses(selected, variant)} data-testid={CARD_TEST_IDS.BADGE}>
              {badgeText}
            </span>
          )}
          {(showInfoIcon || showTooltip) && (
            <div
              onKeyDown={e => {
                onEnter(e, infoClicked);
              }}
              onClick={infoClicked}
              className={getInfoSpaceClasses(selected)}
            >
              <div className={getInfoIconClasses(selected, isDark)} data-testid={CARD_TEST_IDS.INFO_BTN}>
                <i className="susu-icons" translate="no">
                  info
                </i>
              </div>
            </div>
          )}
        </div>
        <div className={getCardDetailsClasses(!!hideMobileInfoText, variant)}>
          <div
            className={cn(CARD_TEXT_CAPITALIZE, getCardTextClasses(selected, warning, variant))}
            data-testid={CARD_TEST_IDS.TEXT}
          >
            {cardText}
          </div>
          {cardSubText && (
            <div className={getCardSubTextClasses(warning, variant)} data-testid={CARD_TEST_IDS.SUBTEXT}>
              {cardSubText}
            </div>
          )}
          {cardSubAdditionalText && (
            <div className={getCardSubTextClasses(warning, variant)} data-testid={CARD_TEST_IDS.SUBTEXT_ADDITIONAL}>
              {cardSubAdditionalText}
            </div>
          )}
        </div>
        {isParent && (
          <div className={getArrowIconClasses(variant)}>
            <i className="susu-icons" translate="no">
              arrow_right
            </i>
          </div>
        )}
      </div>
    );
  },
);

Card.displayName = 'Card';
